//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import React, { ReactNode } from 'react';
import styled from 'styled-components';

import { InvisibleButton } from '@components/atoms/button/Button';
import Icon from '@components/atoms/icon/Icon';
import { Mixins } from '@theme/mixins';
import { ITrackableComponent, withTracking } from '@utils/withTracking';

type AlertType = 'success' | 'warning' | 'error' | 'info';

interface AlertRootProps {
  className?: string;
  children: ReactNode;
  type?: AlertType;
}

const AlertRoot = React.forwardRef<HTMLDivElement, AlertRootProps>(
  ({ children, className, type }, ref) => {
    return (
      <StyledAlertRoot ref={ref} className={className} role='alert' type={type}>
        {children}
      </StyledAlertRoot>
    );
  },
);
AlertRoot.displayName = 'AlertRoot';

interface AlertIconProps {
  className?: string;
  children: ReactNode;
}

const AlertIcon = React.forwardRef<HTMLDivElement, AlertIconProps>(
  ({ children, className }, ref) => {
    return (
      <StyledAlertIcon ref={ref} className={className}>
        {children}
      </StyledAlertIcon>
    );
  },
);
AlertIcon.displayName = 'AlertIcon';

interface AlertContentProps {
  className?: string;
  children: ReactNode;
}

const AlertContent = React.forwardRef<HTMLDivElement, AlertContentProps>(
  ({ children, className }, ref) => {
    return (
      <StyledAlertContent ref={ref} className={className}>
        {children}
      </StyledAlertContent>
    );
  },
);
AlertContent.displayName = 'AlertContent';

interface AlertDismissButtonProps extends ITrackableComponent {
  className?: string;
  onDismiss: () => void;
  type?: AlertType;
}

const AlertDismissButton = ({
  className,
  onDismiss,
  trackingId,
  type,
}: AlertDismissButtonProps) => (
  <StyledInvisibleButton
    aria-label='close'
    className={className}
    onClick={onDismiss}
    trackingId={trackingId}
  >
    <StyledCloseIcon name='close' type={type} />
  </StyledInvisibleButton>
);
AlertDismissButton.displayName = 'AlertDismissButton';

// TODO PD-262522: Change warning color to orange8 and check all uses where content may need
// to have child components' colors changed
type ColorTypes = 'red8' | 'teal1' | 'green8' | 'orange6';
const AlertColors: Record<AlertType, ColorTypes> = {
  error: 'red8',
  info: 'teal1',
  success: 'green8',
  warning: 'orange6',
};
type BgColorTypes = 'red0' | 'cyan0' | 'green0' | 'orange0';
const AlertBgColors: Record<AlertType, BgColorTypes> = {
  error: 'red0',
  info: 'cyan0',
  success: 'green0',
  warning: 'orange0',
};

const StyledAlertRoot = styled.div<AlertRootProps>(
  ({ theme, type = 'info' }) => {
    const color = theme.colors[AlertColors[type]];
    const backgroundColor = theme.colors[AlertBgColors[type]];

    return `
    ${Mixins.Text.Body}
    position: relative;
    padding: ${theme.spacings[3]};
    padding-left: 40px;
    margin-bottom: ${theme.spacings[3]};
    border: 1px solid ${color};
    border-radius: ${theme.borderRadii[1]};
    color: ${color};
    background-color: ${backgroundColor};
    display: flex;
    align-items: normal;
  `;
  },
);

const StyledAlertIcon = styled.span(
  ({ theme }) => `
    position: absolute;
    top: ${theme.spacings[3]};
    left: ${theme.spacings[3]};
    margin-top: ${theme.spacings[1]};
  `,
);

const StyledAlertContent = styled.span(
  () => `
  flex-grow: 1;
  a {
    text-decoration: underline;
  }
`,
);

const StyledInvisibleButton = withTracking(
  styled(InvisibleButton)(
    ({ theme }) => `
    margin-left: auto;
    align-self: baseline;
    height: fit-content;
    padding-top: ${theme.spacings[1]};
  `,
  ),
);

const StyledCloseIcon = styled(Icon)<{ type?: AlertType }>(({
  theme,
  type = 'info',
}) => {
  const color = theme.colors[AlertColors[type]];

  return `color: ${color};`;
});

/**
 * `AlertBox` is a flexible and accessible alert component
 * capable of containing a variety of Icon and Content components.
 *
 * @example
 * This example showcases the `AlertBox` with custom icon and content.
 *
 * return (
 *   <AlertBox.Root type='success'>
 *     <AlertBox.Icon>
 *       <Icon name="bell" />
 *     </AlertBox.Icon>
 *     <AlertBox.Content>
 *       This is a custom AlertBox!
 *     </AlertBox.Content>
 *     <AlertBox.DismissButton
 *       onDismiss={() => {}}
 *       type='success'
 *       trackingId='exampleTrackingId'
 *     />
 *   </AlertBox.Root>
 * );
 */
export const AlertBox = {
  Root: AlertRoot,
  Icon: AlertIcon,
  Content: AlertContent,
  DismissButton: AlertDismissButton,
};
